define([
  'underscore',
  'immutable',
  'es6!sumo/util',
  'es6!launcher/util/listbuilder/constants',
], (_, Immutable, Util, Constants) => {
  const { FormTypes } = Constants;
  const { containsPrize } = Util;

  function updateComponentGlobals(globals, component) {
    const meta = component.getIn(['shortcutMeta', 'meta']);
    if (!meta) return component;

    const updateGlobals = (v) => {
      let vars = v;

      vars.keySeq().forEach((key) => {
        const globalKey = vars.get(key);
        if (typeof globalKey === 'string' && globals.has(globalKey)) {
          vars = vars.set(key, globals.get(globalKey));
        } else if (Immutable.Map.isMap(globalKey)) {
          vars = vars.mergeIn([key], updateGlobals(globalKey));
        }
      });

      return vars;
    };

    return component.mergeDeep(updateGlobals(meta));
  }

  function cloneJSON(json) {
    return JSON.parse(JSON.stringify(json || {}));
  }

  function isPercent(shouldCheck, value) {
    return shouldCheck && value && value.toString().indexOf('%') !== -1;
  }

  function getPixelValue(pixelString) {
    // console.assert(!pixelString || pixelString.slice(-2) === 'px', 'getPixelValue received non px string');

    return parseInt(pixelString, 10);
  }

  function getPercentValue(percentString) {
    if (!percentString || !percentString.length) return NaN;
    if (!/^([1-9][0-9]*|0)%$/.test(percentString)) return NaN;

    return parseInt(percentString.substring(0, percentString.length - 1), 10);
  }

  function getRoundedPosition(position, respectPercentages) {
    return {
      left: isPercent(respectPercentages, position.left) ? position.left : Math.round(position.left),
      top: isPercent(respectPercentages, position.top) ? position.top : Math.round(position.top),
      width: isPercent(respectPercentages, position.width) ? position.width : Math.round(position.width),
      height: isPercent(respectPercentages, position.height) ? position.height : Math.round(position.height),
    };
  }

  function styleToPosition(style, respectPercentages) {
    const styleCopy = _.clone(style) || {};

    const left = isPercent(respectPercentages, styleCopy.left)
      ? styleCopy.left
      : (getPixelValue(styleCopy.left) || 0);

    const top = isPercent(respectPercentages, styleCopy.top)
      ? styleCopy.top
      : (getPixelValue(styleCopy.top) || 0);

    const width = isPercent(respectPercentages, styleCopy.width)
      ? styleCopy.width
      : (getPixelValue(styleCopy.width) || 0);

    const height = isPercent(respectPercentages, styleCopy.height)
      ? styleCopy.height
      : (getPixelValue(styleCopy.height) || 0);

    return { left, top, width, height };
  }

  function positionToStyle(position, respectPercentages) {
    const { left, top, width, height } = position;

    const style = {};

    if (isPercent(respectPercentages, left)) {
      style.left = left;
    } else if (typeof left !== 'undefined' && left !== null) {
      style.left = `${left}px`;
    }

    if (isPercent(respectPercentages, top)) {
      style.top = top;
    } else if (typeof top !== 'undefined' && top !== null) {
      style.top = `${top}px`;
    }

    if (isPercent(respectPercentages, width)) {
      style.width = width;
    } else if (typeof width !== 'undefined' && width !== null) {
      style.width = `${width}px`;
    }

    if (isPercent(respectPercentages, height)) {
      style.height = height;
    } else if (typeof height !== 'undefined' && height !== null) {
      style.height = `${height}px`;
    }

    return style;
  }

  /**
   * Returns the components for the given `popup` prepared for the given `responsiveFormat`
   * @param  {Immutable.List} components        The popup to prepare responsive componets from
   * @param  {String}         responsiveFormat  Format use when preparing components (default|mobile)
   * @return {Immutable.List}                   An Immutable List of components in the given `responsiveFormat`
   */
  function getComponentsForResponsiveFormat(components, responsiveFormat) {
    const getNestedComponents = (components, responsiveFormat) => {
      if (!components || !components.size) return [];

      let responsiveComponents = Immutable.List();
      components.forEach((component) => {
        const responsiveHiddenOn = component.get('responsiveHiddenOn');
        const childComponents = component.get('childComponents');

        if (responsiveHiddenOn && responsiveHiddenOn.get(responsiveFormat)) return;

        if (childComponents && childComponents.size) {
          responsiveComponents = responsiveComponents.concat(getNestedComponents(childComponents, responsiveFormat));
        } else {
          responsiveComponents = responsiveComponents.push(component);
        }
      });

      return responsiveComponents;
    }

    return getNestedComponents(components, responsiveFormat);
  }

  function hasDiscount(popup, autoResponseEmails) {
    const parsedJson = popup.get('parsedJson');

    if (!parsedJson) return false;
    
    const goal = popup.get('goal');
    const subscribeRedirectUrl = popup.get('subscribe_redirect_url') || '';
    const conversionPixels = popup.get('pixels_conversion') || '';
    const components = parsedJson.getIn(['success', 'components']) || Immutable.List();

    for (let i = 0; i < components.size; i += 1) {
      const component = components.get(i);
      const type = component.get('type');
      const text = component.get('text');
      const to = component.get('to');
      const action = component.get('action');

      if (type === 'discount') {
        return true;
      }

      if (popup.get('form_type') !== 'game') {
        if (['text', 'button'].indexOf(type) > -1) {
          if (
            containsPrize(text)
            || (action === 'redirect' && containsPrize(to))
          ) {
            return true;
          }
        }
      }
    }

    // Check subscribe redirect URL and conversion pixels
    if (containsPrize(subscribeRedirectUrl) || containsPrize(conversionPixels)) return true;

    // TODO: Update this somehow
    // Check auto response emails if form is collecting emails
    if (goal === 'emails') {
      const autoResponses = popup.get('auto_responses');
      if (autoResponses && autoResponses.size) {
        for (let i = 0; i < autoResponses.size; i += 1) {
          if (autoResponses.getIn([i, 'status']) === 'deleted') continue;
          const rule = autoResponses.getIn([i, 'email']);
          const email = autoResponseEmails.find(e => e.get('id') === rule.get('id'));
          if (email && email.get('body') && email.get('body').indexOf('{discount}') > -1) {
            return true;
          }
        }
      }
    }

    return false;
  }

  // Checks game component and makes sure there is at least one discount campaign assigned.
  function hasValidPrizes(popup, discountCampaignIds) {
    const components = popup.getIn(['parsedJson', 'subscribe', 'components']);
    const gameComponent = components.find(component => component.get('type') === 'game');

    if (!gameComponent) return false;

    const options = gameComponent.get('options');

    const winningOption = options.find((option) => {
      const prizeType = option.get('prizeType');
      const discountCampaignId = option.get('discountCampaignId');
      const weight = option.get('weight');

      return (
        prizeType &&
        prizeType !== 'none' &&
        (prizeType !== 'discount' || discountCampaignIds.contains(discountCampaignId)) &&
        weight > 0
      );
    });

    return winningOption;
  }

  function hasDeletedPrizes(popup, discountCampaignIds) {
    const components = popup.getIn(['parsedJson', 'subscribe', 'components']);
    const gameComponent = components.find(component => component.get('type') === 'game');

    if (!gameComponent) return false;

    const options = gameComponent.get('options');

    const invalidDiscount = options.find(option => (
      option.get('prizeType') === 'discount' &&
      discountCampaignIds.indexOf(option.get('discountCampaignId')) === -1
    ));

    return !!invalidDiscount;
  }

  function canUseTabs(formType) {
    return [
      FormTypes.SCROLL_BOX,
      FormTypes.POPUP,
      FormTypes.CLICK_TRIGGER_POPUP,
      FormTypes.GAME,
    ].indexOf(formType) > -1;
  }

  function isInlineComponent(component) {
    return component.type !== 'background' && (component.type !== 'image' || (['close', 'optout'].indexOf(component.action) === -1 && !component.footer));
  }

  function getHeadingText(json) {
    const defaultHeading = 'Join Our Newsletter';
    try {
      if ('subscribe' in json) {
        const headingComponent = _.first(_.filter(json.subscribe.components, function(c) {
          return (c.type === 'text');
        }).sort(function(a, b) {
          const aFontSize = parseFloat((a.style.fontSize || '12').replace('px', ''));
          const bFontSize = parseFloat((b.style.fontSize || '12').replace('px', ''));

          return bFontSize - aFontSize;
        }));

        return headingComponent ? headingComponent.text : defaultHeading;
      }
    } catch(e) {
      return defaultHeading;
    }
  }

  function getReadableDate(date) {
    const month = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'June', 'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.'];
    return `${month[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  }

  function generatePopupName(json, d = new Date()) {
    const headingText = getHeadingText(json);
    if (isNaN(d.getTime())) d = new Date();

    const date = getReadableDate(d);
    return `${date} - ${headingText}`;
  }

  const genId = () => {
    const r = () => `${Math.random().toString(16)}00000000`.substr(2, 8).toLowerCase();
    return r() + r() + r() + r() + r() + r() + r() + r();
  };

  return {
    canUseTabs,
    cloneJSON,
    generatePopupName,
    getComponentsForResponsiveFormat,
    getPercentValue,
    getPixelValue,
    getRoundedPosition,
    hasDeletedPrizes,
    hasDiscount,
    hasValidPrizes,
    isInlineComponent,
    positionToStyle,
    styleToPosition,
    updateComponentGlobals,
    genId,
  };
});
